/**
 * @module core/filter/constants
 * @description 筛选运算符展示选项（字符串/数值），与 `Operation` 取值对齐。
 *
 * @core SortableDrag、Dashboard TopFilter、UserFilter、探索分析等共用。
 */

import { Operation } from './types';
import type { IFilterOperationOption } from './types';

/**
 * 筛选值在 UI（Tag / 下拉）中表示 SQL `NULL` 与空字符串的 sentinel 占位符。
 *
 * 与存量 `getNullTextOfFilter`（`utils/utils.ts`）及 `StringSelectorWithTag` 往返逻辑对齐；
 * 新代码引用本常量，消费方迁移后再替换硬编码 `'[NULL]'` / `'[empty string]'`。
 *
 * @see `doc/refactor/docs/dual-state-field-schema.md` §1.4 filter 空值语义
 */
export const FilterNullToken = {
  Null: '[NULL]',
  EmptyString: '[empty string]',
} as const;

export type TFilterNullToken =
  (typeof FilterNullToken)[keyof typeof FilterNullToken];

/** 字符串维度筛选：包含 / 排除（value 对齐 `Operation.Include` / `Operation.Exclude`）。 */
export const FilterStringOptions: IFilterOperationOption[] = [
  {
    value: Operation.Include,
    text: '包含',
    text_en: 'Include',
  },
  {
    value: Operation.Exclude,
    text: '排除',
    text_en: 'Exclude',
  },
];

/** 数值字段可用的比较运算符选项（展示文案）。 */
export const NUMBER_OPERATION_OPTIONS: IFilterOperationOption[] = [
  {
    value: Operation.Greater,
    text: '大于(>)',
    text_en: 'Greater(>)',
  },
  {
    value: Operation.Less,
    text: '小于 (<)',
    text_en: 'Less (<)',
  },
  {
    value: Operation.GreaterOrEqual,
    text: '大于等于 (≥)',
    text_en: 'Greater or Equal (≥)',
  },
  {
    value: Operation.LessOrEqual,
    text: '小于等于(≤)',
    text_en: 'Less or Equal (≤)',
  },
  {
    value: Operation.Equal,
    text: '等于 (=)',
    text_en: 'Equal (=)',
  },
  {
    value: Operation.NotEqual,
    text: '不等于(≠)',
    text_en: 'Not equal(≠)',
  },
  {
    value: Operation.Between,
    text: '区间',
    text_en: 'Between',
  },
  {
    value: Operation.NotNul,
    text: '有值',
    text_en: 'Non-null Values',
  },
  {
    value: Operation.Null,
    text: '空值',
    text_en: 'Null Values',
  },
];
